import { z } from 'zod';

/**
 * Permission response schema.
 */
export const permissionResponseSchema = z.object({
  id: z.string().uuid(),
  module: z.string(),
  action: z.string(),
  description: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export type PermissionResponse = z.infer<typeof permissionResponseSchema>;

/**
 * Role permission schema (permission assigned to a role with optional scope).
 */
export const rolePermissionSchema = z.object({
  id: z.string().uuid(),
  module: z.string(),
  action: z.string(),
  scope: z.enum(['all', 'own']).nullable(),
});

export type RolePermissionResponse = z.infer<typeof rolePermissionSchema>;

/**
 * Role response schema.
 */
export const roleResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  slug: z.string(),
  isSystem: z.boolean(),
  rowStatus: z.number().int(),
  createdBy: z.string().uuid().nullable(),
  updatedBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type RoleResponse = z.infer<typeof roleResponseSchema>;

/**
 * Role with permissions response.
 */
export const roleWithPermissionsSchema = z.object({
  role: roleResponseSchema,
  permissions: z.array(rolePermissionSchema),
});

export type RoleWithPermissions = z.infer<typeof roleWithPermissionsSchema>;

/**
 * Module permission metadata (from shared RBAC types).
 */
export const modulePermissionSchema = z.object({
  permission: z.string(),
  label: z.string(),
  scopeable: z.boolean(),
});

export const permissionModuleSchema = z.object({
  module: z.string(),
  label: z.string(),
  permissions: z.array(modulePermissionSchema),
});

export type PermissionModuleResponse = z.infer<typeof permissionModuleSchema>;

/**
 * Permissions list response (includes both raw permissions and module grouping).
 */
export const permissionsListSchema = z.object({
  permissions: z.array(permissionResponseSchema),
  modules: z.array(permissionModuleSchema),
});

export type PermissionsListResponse = z.infer<typeof permissionsListSchema>;

/**
 * Create role request schema.
 */
export const createRoleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  slug: z.string().min(1, 'Slug is required'),
  permissions: z.array(
    z.object({
      permissionId: z.string().uuid(),
      scope: z.enum(['all', 'own']).nullable(),
    })
  ),
});

export type CreateRoleRequest = z.infer<typeof createRoleSchema>;

/**
 * Update role request schema.
 */
export const updateRoleSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  permissions: z
    .array(
      z.object({
        permissionId: z.string().uuid(),
        scope: z.enum(['all', 'own']).nullable(),
      })
    )
    .optional(),
});

export type UpdateRoleRequest = z.infer<typeof updateRoleSchema>;
